import { Link } from 'react-router-dom';
import { shortEpisodeLabel } from '@/lib/format';
import { StateMessage } from '@/components/ui/StateMessage';
import type { AnimeServer } from '@/types/anime';

interface EpisodePickerProps {
	servers: AnimeServer[];
	activeServerIndex: number;
	onServerChange: (index: number) => void;
	activeEpisodeSlug: string | null;
	/** Trả về href cho từng tập, để mỗi tập là một URL chia sẻ được. */
	buildEpisodeHref: (episodeSlug: string) => string;
}

export function EpisodePicker({
	servers,
	activeServerIndex,
	onServerChange,
	activeEpisodeSlug,
	buildEpisodeHref,
}: EpisodePickerProps) {
	if (servers.length === 0) {
		return (
			<StateMessage
				icon="🎬"
				title="Chưa có link phát"
				description="Kho phim chưa cập nhật tập nào cho tựa này. Quay lại sau nhé."
			/>
		);
	}

	const activeServer = servers[activeServerIndex] ?? servers[0];

	return (
		<div>
			{servers.length > 1 && (
				<div
					className="mb-3 flex flex-wrap gap-2"
					role="tablist"
					aria-label="Chọn server"
				>
					{servers.map((server, index) => (
						<button
							key={`${server.name}-${index}`}
							type="button"
							role="tab"
							aria-selected={index === activeServerIndex}
							onClick={() => onServerChange(index)}
							className={`rounded-lg border px-3 py-1.5 text-xs font-bold transition ${
								index === activeServerIndex
									? 'border-iris bg-iris/20 text-ink'
									: 'border-line bg-white/[0.03] text-muted hover:border-iris hover:text-ink'
							}`}
						>
							{server.name}
							<span className="ml-1.5 font-normal opacity-70">
								{server.episodes.length}
							</span>
						</button>
					))}
				</div>
			)}

			<div className="flex max-h-[228px] flex-wrap gap-2 overflow-y-auto pr-1 scrollbar-slim">
				{activeServer?.episodes.map((episode) => {
					const selected = episode.slug === activeEpisodeSlug;
					return (
						<Link
							key={episode.slug}
							to={buildEpisodeHref(episode.slug)}
							title={episode.name}
							aria-current={selected ? 'true' : undefined}
							className={`min-w-11 rounded-[7px] border px-2.5 py-1.5 text-center text-sm transition ${
								selected
									? 'border-accent bg-accent/15 font-bold text-ink'
									: 'border-line bg-white/[0.03] text-muted hover:border-accent hover:text-ink'
							}`}
						>
							{shortEpisodeLabel(episode.name)}
						</Link>
					);
				})}
			</div>
		</div>
	);
}
